import { createApp, watch } from 'vue';
import ProjectSearchBar from './components/ProjectSearchBar.vue';
import ProjectToolbar from './components/ProjectToolbar.vue';
import ProjectResults from './components/ProjectResults.vue';
import ProjectDetailPanel from './components/ProjectDetailPanel.vue';
import { createProjectsApi } from './lib/projectsApi';
import { toProjectsApiParams } from './lib/projectsApiParams';
import { filterConfig } from './lib/filterConfig';
import { routes } from './lib/routes';
import { configureProjectFilterSets } from './lib/projectsContract';
import { useProjectCriteria } from './lib/useProjectCriteria';
import { useMapState } from './lib/useMapState';
import { useResults } from './lib/useResults';
import { useView } from './lib/useView';
import { setTranslations } from './lib/i18n';

const mountEl = document.querySelector('#projects-search');
const toolbarEl = document.querySelector('#projects-toolbar');
const resultsEl = document.querySelector('#projects-results');
const detailEl = document.querySelector('#projects-detail');
// A region page has no search bar, so its #projects-results carries the
// bootstrap data instead of #projects-search.
const bootstrapEl = mountEl || resultsEl;

const SEARCH_DEBOUNCE_MS = 300;

if (bootstrapEl) {
  setTranslations(JSON.parse(bootstrapEl.dataset.listingTranslations || '{}'));

  const regions = JSON.parse(bootstrapEl.dataset.projectRegions || '[]');
  const statuses = JSON.parse(bootstrapEl.dataset.projectStatuses || '[]');
  const defaultStatuses = JSON.parse(bootstrapEl.dataset.projectDefaultStatuses || '[]');
  const fixedRegion = bootstrapEl.dataset.fixedRegion || null;

  // Before the first useProjectCriteria() call: the querystring's regions and
  // statuses are validated against these backend-injected sets.
  configureProjectFilterSets({ regions, statuses, defaults: defaultStatuses });

  const api = createProjectsApi();
  const { criteria, toQueryString, persist, reset } = useProjectCriteria();

  // A region page shows only its own region, no other filter: drop whatever
  // the querystring/localStorage produced (statuses, sort, location radius,
  // ...) and lock regions to this page's region. There is no filter bar
  // mounted to change any of it back.
  if (fixedRegion) {
    reset();
    criteria.regions = [fixedRegion];
  }

  const { bbox, clearBbox, requestFit } = useMapState();
  const { view } = useView();

  // The list view shows everything within the filters, so it ignores the map
  // viewport; the stored bbox is kept, not cleared, so switching back to
  // both/map restores the refined viewport and its result set
  const activeBbox = () => (view.value === 'list' ? null : bbox.value);
  const { results, setLoading, setData, setError, setLoadingMore, appendData, stopLoadingMore } =
    useResults();

  // One row: result count, filters, view toggles. A region page has no filters
  // of its own, so it gets the count and the toggles only.
  if (toolbarEl) {
    createApp(ProjectToolbar, { regions, statuses, showFilters: !fixedRegion }).mount(toolbarEl);
  }

  // a failed append quietly re-arms instead of replacing loaded cards with
  // the error state
  const loadMore = async () => {
    if (results.loading || results.loadingMore || !results.meta?.hasMore) return;
    const token = setLoadingMore();
    try {
      appendData(token, await api.search(criteria, results.meta.page + 1, activeBbox()));
    } catch {
      stopLoadingMore();
    }
  };

  if (resultsEl) {
    createApp(ProjectResults, { onLoadMore: loadMore }).mount(resultsEl);
  }

  if (detailEl) {
    createApp(ProjectDetailPanel).mount(detailEl);
  }

  // Any criteria change keeps the URL canonical: the query string is the
  // shareable source of truth.
  watch(
    () => toQueryString(),
    (queryString) => {
      window.history.replaceState(null, '', `${window.location.pathname}${queryString ? `?${queryString}` : ''}`);
    }
  );

  let searchTimer = null;
  const runSearch = async () => {
    const token = setLoading();
    try {
      setData(token, await api.search(criteria, 1, activeBbox()));
    } catch (error) {
      setError(token, error.message || 'Zoeken mislukt');
    }
  };

  // A new location redefines the search area, so the viewport bbox no longer
  // applies. Both changes land in the same debounce window below, so this stays
  // one request. No fit is requested here: the map re-frames itself on this same
  // change (see its own long/lat/radius watch).
  watch(
    () => [criteria.long, criteria.lat, criteria.radius],
    clearBbox
  );

  // A region moves the result set to a different part of the country, so the
  // camera has to follow or the new markers sit outside the current view
  // (WHN-2128). Status and sort only thin out what is already in view, so those
  // keep both the camera and the user's zoom.
  watch(
    () => criteria.regions.join(','),
    () => {
      clearBbox();
      requestFit();
    }
  );

  // Re-query whenever the API querystring changes; debounced so rapid filter
  // changes fire one request. Immediate to fetch on load.
  watch(
    () => toProjectsApiParams(criteria, 1, activeBbox()).toString(),
    () => {
      clearTimeout(searchTimer);
      searchTimer = setTimeout(runSearch, SEARCH_DEBOUNCE_MS);
    },
    { immediate: true }
  );

  if (mountEl) {
    const app = createApp(ProjectSearchBar, {
      api,
      bootstrap: filterConfig,
      onSearch() {
        persist();
        const queryString = toQueryString();
        window.location.href = `${routes.projects}${queryString ? `?${queryString}` : ''}`;
      },
      onPickProject(project) {
        if (project && project.url) window.open(project.url, '_blank', 'noopener');
      }
    });

    app.mount(mountEl);
  }
}
